import { ActionFunctionArgs, HeadersFunction, LoaderFunctionArgs, json } from '@remix-run/node';
import { useLoaderData, useSubmit } from '@remix-run/react';
import { KlaviyoEvent, pushKlaviyoEvent } from '@theprintbar/bullmq';
import { GetContentPageDocument, GetWarehousesDocument } from '@theprintbar/graphql';
import { runSudoGraphql } from '@theprintbar/keystone/context';
import { cachedJsonDay, getS3Url, getSecureAssetUrl, globalFormError, serverEnv, toastSuccess, withEnsuredForm } from '@theprintbar/server';
import { WebRoutes, invariantInvalid, logError, withDomain } from '@theprintbar/shared';
import {
	ActionToast,
	Checkbox,
	Container,
	DragAndDropArea,
	FullCTA,
	GoogleMap,
	GoogleMapProvider,
	HoneypotForm,
	Input,
	PrimaryButton,
	RichText,
	Select,
	TextArea,
} from '@theprintbar/ui';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { getSearchParams } from 'remix-params-helper';
import { useRootData } from '~/root';
import { contentMeta } from '~/utils/contentMeta';
import { contactFormParams, contactSchemaValidator, contactServerSchemaValidator } from './Contact.schema';

export const meta = contentMeta;

export const headers: HeadersFunction = ({ loaderHeaders }) => ({
	'Cache-Control': loaderHeaders.get('Cache-Control') || '',
});

export async function action({ request }: ActionFunctionArgs) {
	return withEnsuredForm(
		{
			validator: contactServerSchemaValidator,
			request,
			recaptcha: true,
			bucketUpload: true,
		},
		async ({ attachments, ...form }) => {
			try {
				const attachmentURLs = await Promise.all(
					attachments?.map(async attachment => {
						return {
							fileName: attachment.file,
							url: await getSecureAssetUrl(attachment.file),
						};
					}) || [],
				);

				await pushKlaviyoEvent.addUniqueJob(`contact-${form.email}`, {
					event: KlaviyoEvent.Contact,
					data: {
						email: form.email,
						firstName: form.firstName,
						lastName: form.lastName,
						subject: form.subject,
						message: form.message,
						newsletter: form.newsletter,
						attachments: attachmentURLs,
						source: form.source,
					},
				});

				return toastSuccess({ title: 'Thanks for reaching out!', message: 'Your message has been sent' });
			} catch (e) {
				await logError(e);
				return globalFormError('There was a problem sending your message. Please try again later.');
			}
		},
	);
}

export async function loader({ request }: LoaderFunctionArgs) {
	const [{ contentPage }, { warehouses }] = await Promise.all([
		runSudoGraphql(GetContentPageDocument, { where: { slug: 'contact' } }),
		runSudoGraphql(GetWarehousesDocument),
	]);

	if (!contentPage) throw json('Not Found', { status: 404 });

	const params = getSearchParams(request, contactFormParams);
	invariantInvalid(params.success, JSON.stringify(params.errors));

	return cachedJsonDay({
		contentPage,
		params,
		warehouses,
		gcpKey: serverEnv.GCP_API_KEY,
	});
}

export default function () {
	const { contentPage, gcpKey, warehouses, params } = useLoaderData<typeof loader>();
	const { globalContent, recaptchaSiteKey, isDev } = useRootData();
	const [attachments, setAttachments] = useState<File[]>([]);
	const attachmentsRef = useRef(attachments);
	const submit = useSubmit();

	useEffect(() => {
		attachmentsRef.current = attachments;
	}, [attachments]);

	const handleAttachments = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const newAttachments = e.target.files?.length
				? Array.from(e.target.files).filter(a => !attachments.some(({ name }) => name === a.name))
				: [];
			setAttachments([...attachments, ...newAttachments]);
		},
		[attachments],
	);

	const handleSubmit = useCallback(
		async (e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault();
			const target = e.target as HTMLFormElement;
			const files = attachmentsRef.current;

			const formData = new FormData(target);
			files.forEach((file, index) => {
				formData.append(`attachments[${index}].file`, file);
				formData.append(`attachments[${index}].size`, file.size.toString());
			});

			submit(formData, {
				method: 'POST',
				encType: 'multipart/form-data',
			});
		},
		[attachments],
	);

	useEffect(() => {
		// Check if there's a hash in the URL
		if (params.data.subject || params.data.source) {
			// Ensure the code runs only on the client-side
			if (typeof window !== 'undefined') {
				const element = document.getElementById('contact-form');
				if (element) {
					element.scrollIntoView({ behavior: 'smooth' });
				}
			}
		}
	}, []);

	return (
		<div>
			<ActionToast />
			<h1 className="hidden">{contentPage.headingTag || contentPage.metaTitle}</h1>
			<FullCTA cta={contentPage.fullRoundedCTA} rounded hero={true} />

			<Container className="max-w-8xl">
				<div className="grid grid-cols-1 lg:grid-cols-2 mx-4 lg:mx-8 my-10 lg:my-14 gap-24 lg:gap-36">
					<GoogleMapProvider apiKey={gcpKey}>
						{warehouses?.map(warehouse => (
							<div key={warehouse.id} className="space-y-8 lg:space-y-10">
								<h2 className="font-light text-2xl lg:text-3xl">
									{warehouse.traditionalName} / {warehouse.name}
								</h2>
								<GoogleMap
									width="w-full"
									height="h-96"
									location={{ lat: parseFloat(warehouse.latitude), lng: parseFloat(warehouse.longitude) }}
								/>
								<div className="grid grid-cols-2 gap-8">
									<div>
										<h5 className="text-xs mb-3 uppercase">Address</h5>
										<a href="#" className="text-sm lg:text-base">
											{warehouse.streetAddress},<br />
											{warehouse.traditionalCountry},<br />
											{warehouse.state} {warehouse.postCode}, {warehouse.country}
										</a>
									</div>
									<div>
										<h4 className="text-xs mb-3">Opening hours</h4>
										<RichText className="text-sm lg:text-base" content={warehouse.openingHours} />
									</div>
									<div>
										<h5 className="text-xs mb-3 uppercase">Email</h5>
										<a href={`mailto:${warehouse.email}`} className="text-sm lg:text-base">
											{warehouse.email}
										</a>
									</div>
									<div>
										<h5 className="text-xs mb-3 uppercase">Phone number</h5>
										<a href={`tel:${warehouse.phoneNumber}`} className="text-sm lg:text-base">
											{warehouse.phoneNumber}
										</a>
									</div>
								</div>
							</div>
						))}
					</GoogleMapProvider>
				</div>
			</Container>

			{/* CUSTOM CONTACT US FORM */}
			<div className="bg-blue-bright/20 py-16 lg:py-28 px-4 lg:px-0" id="contact-form">
				<HoneypotForm
					honeyField="messsageImportance"
					gaCategory="Contact"
					gaLabelKey="subject"
					validator={contactSchemaValidator}
					recaptchaKey={recaptchaSiteKey}
					recaptchaAction="contact"
					className="w-full max-w-lg mx-auto"
					encType="multipart/form-data"
					onSubmit={(_, e) => handleSubmit(e)}
				>
					<h5 className="text-lg uppercase mb-4">Contact US</h5>
					<div className="flex flex-col">
						<input type="hidden" name="source" value={params.data.source} />
						<Input label="Email address" name="email" placeholder="Email address" />
						<div className="grid grid-cols-2 gap-2">
							<Input label="First name" name="firstName" placeholder="First name" />
							<Input label="Last name" name="lastName" placeholder="Last name" />
						</div>
						<Select label="What's your message about?" labelClassName="text-blue" name="subject">
							{globalContent.contactSubjects.structure?.map(subject =>
								!!subject ? (
									<option key={subject} value={subject}>
										{subject}
									</option>
								) : null,
							)}
							{params.data.subject && !globalContent.contactSubjects.structure?.find(s => s === params.data.subject) ? (
								<option selected value={params.data.subject}>
									{params.data.subject}
								</option>
							) : null}
						</Select>
						<TextArea label="Message" name="message" placeholder="Message" />
						<DragAndDropArea
							label="Need to share your design? Upload it here"
							accept="*"
							multiple
							outsideForm
							name="files"
							onChange={handleAttachments}
							className="w-full h-48 mb-4 border-blue"
							labelClassName="text-blue text-sm"
						/>
						{!!attachments.length && (
							<ul className="space-y-2 ml-4 mb-4">
								{attachments.map(file => (
									<li key={file.name} className="list-disc text-xs text-blue">
										<div className="flex justify-between items-center">
											<span>{file.name}</span>
										</div>
									</li>
								))}
							</ul>
						)}
						<Checkbox label="Receive our occasional newsletter" name="newsletter" />
						<PrimaryButton type="submit" className="w-full">
							Submit
						</PrimaryButton>
					</div>
				</HoneypotForm>
			</div>
		</div>
	);
}
